<template>
  <thead class="light-thead">
    <tr @contextmenu="onOpenMenu">
      <th
        v-if="config.menu"
        style="width: 20px; text-align: center"
        class="service-th"
      >
        <a href="#" @click="onOpenMenu">
          <i class="icon icon-menu"></i>
        </a>
      </th>
      <th
        v-if="config.rowSelect"
        style="width: 25px; text-align: center"
        class="service-th"
      >
        <input
          type="checkbox"
          class="row-checkbox"
          @click="selectRows"
          :indeterminate="store.data.rowsSelected === null"
          :checked="store.data.rowsSelected"
        />
      </th>
      <th
        v-if="config.numbering"
        style="width: 40px; text-align: center"
        class="service-th"
      >
        №
      </th>
      <template v-for="fieldkey of sortedFields" :key="fieldkey">
        <template v-if="visible(getField(fieldkey))">
          <th
            :style="getStyleTh(getField(fieldkey), fieldkey)"
            :class="getThClasses(fieldkey)"
            @click="(event) => onClickCell(event, fieldkey, getField(fieldkey))"
            :draggable="config.columnDrag !== false"
            @dragstart="(e) => onDragStart(e, fieldkey)"
            @dragover="(e) => onDragOver(e, fieldkey)"
            @dragleave="onDragLeave"
            @drop="(e) => onDrop(e, fieldkey)"
            @dragend="onDragEnd"
          >
            <span v-html="getField(fieldkey).description"></span>
            <ColumnResizer
              v-if="config.columnResize !== false"
              :fieldKey="fieldkey"
              :isResizing="isResizing"
              :currentField="currentResizeField || undefined"
              @startResize="
                (e) => onStartResize(e, fieldkey, getField(fieldkey))
              "
            />
          </th>
        </template>
      </template>
    </tr>
  </thead>
</template>

<script lang="ts">
import openContextMenu from "@/components/openContextMenu";
import ColumnResizer from "./ColumnResizer.vue";
import { useColumnResize } from "./composables/useColumnResize";
import { useColumnDrag } from "./composables/useColumnDrag";
import { defineComponent, nextTick, ref } from "vue";

export default defineComponent({
  components: {
    ColumnResizer,
  },

  emits: ["sort"],

  props: {
    controller: {
      type: Object,
      default: () => ({}),
    },
    checkedRowSelect: {
      type: Boolean,
      default: false,
    },
    stickyHeader: {
      type: Boolean,
      default: false,
    },
  },

  setup(props: any, { emit }: { emit: any }) {
    const store = props.controller.store;

    const config = props.controller.settings;

    const sortingFields: any = props.controller.sortingFields;

    // === Column Resize ===
    const isResizing = ref(false);
    const currentResizeField = ref<string | null>(null);
    const justResized = ref(false);

    const columnResize = useColumnResize({
      minWidth: 50,
      maxWidth: 500,
      onResize: (fieldKey: string, width: number) => {
        // Update width in real-time
        if (store.state.fields[fieldKey]) {
          store.state.fields[fieldKey].config.width = `${width}px`;
        }
      },
      onResizeEnd: (fieldKey: string, width: number) => {
        // Save width on resize end
        if (props.controller.updateColumnWidth) {
          props.controller.updateColumnWidth(fieldKey, width);
        }
        // Reset resizing state
        isResizing.value = false;
        currentResizeField.value = null;
        // Mark that we just finished resizing to prevent click event
        justResized.value = true;
        setTimeout(() => {
          justResized.value = false;
        }, 100);
      },
    });

    const onStartResize = (event: MouseEvent, fieldKey: string, field: any) => {
      isResizing.value = true;
      currentResizeField.value = fieldKey;

      // Get current width from DOM element (most accurate)
      let currentWidth = 100;

      // Try to get width from the parent th element
      const target = event.target as HTMLElement;
      const thElement = target?.closest?.("th");
      if (thElement) {
        currentWidth = thElement.offsetWidth;
      } else if (field.config?.width) {
        // Fallback to config width if DOM element not found
        const configWidth = field.config.width;
        currentWidth =
          typeof configWidth === "string"
            ? parseInt(configWidth, 10)
            : configWidth;
      }

      columnResize.startResize(event, fieldKey, currentWidth);
    };

    // === Column Drag-and-Drop ===
    const columnDrag = useColumnDrag({
      onDrop: (fromKey: string, toKey: string, position: string) => {
        if (props.controller.reorderColumns) {
          props.controller.reorderColumns(fromKey, toKey, position);
        }
      },
    });

    const onDragStart = (event: DragEvent, fieldKey: string) => {
      if (config.columnDrag === false) return;
      columnDrag.handleDragStart(event, fieldKey);
    };

    const onDragOver = (event: DragEvent, fieldKey: string) => {
      if (config.columnDrag === false) return;
      columnDrag.handleDragOver(event, fieldKey);
    };

    const onDragLeave = (event: DragEvent) => {
      columnDrag.handleDragLeave(event);
    };

    const onDrop = (event: DragEvent, fieldKey: string) => {
      if (config.columnDrag === false) return;
      columnDrag.handleDrop(event, fieldKey);
    };

    const onDragEnd = () => {
      columnDrag.handleDragEnd();
    };

    /**
     * Opening the context menu
     */
    const onOpenMenu = (event: any) => {
      if (config.menu) {
        event.preventDefault();

        openContextMenu(event, props.controller.contextMenuHead());
      }
    };

    /**
     * Handling click on header cells
     */
    const onClickCell = (event: any, field: any, fieldConfig: any) => {
      // Don't sort if we're in resize mode or just finished resizing
      if (isResizing.value || justResized.value) return;

      // Don't sort if click was on the resizer element
      const target = event.target as HTMLElement;
      if (target?.closest?.(".column-resizer")) return;

      event.preventDefault();

      if (config.sorting && "sorting" in fieldConfig?.config) {
        if (!event.ctrlKey) {
          for (const key in sortingFields) {
            if (key != field) {
              delete sortingFields[key];
            }
          }
        }

        if (field in sortingFields) {
          if (sortingFields[field]) {
            sortingFields[field] = false;
          } else {
            delete sortingFields[field];
          }
        } else {
          sortingFields[field] = true;
        }

        emit("sort", sortingFields);
      }
    };

    /**
     * Styles for header cells
     */
    const getStyleTh = (fieldConfig: any, fieldKey?: string) => {
      const result: any = {};

      if (fieldConfig.config?.width) {
        result["width"] = fieldConfig.config.width;
      }

      // Add relative positioning for resizer
      result["position"] = "relative";

      return result;
    };

    /**
     * Get all classes for th element
     */
    const getThClasses = (field: any) => {
      const classes: any = {};

      // Sticky class for columns
      const fieldConfig = store.state.fields[field];
      const _config = fieldConfig?.config;

      if (_config?.sticky) {
        classes.sticky = true;
      }

      if (config.sorting && _config?.sorting) {
        if (field in sortingFields) {
          classes.sorting_asc = sortingFields[field];
          classes.sorting_desc = !sortingFields[field];
        } else {
          classes.sorting = true;
        }
      }

      // Drag-and-drop classes
      if (columnDrag.isDraggedField(field)) {
        classes["th-dragging"] = true;
      }

      if (columnDrag.isDropTarget(field)) {
        const dropClass = columnDrag.getDropIndicatorClass(field);
        if (dropClass) {
          classes[dropClass] = true;
        }
      }

      return classes;
    };

    const selectRows = (e: any) => {
      if (e.target.checked && store.data.rowsSelected !== null) {
        store.forEach((row: any) => config.onRowSelect(row, true));
      } else {
        store.forEach((row: any) => config.onRowSelect(row, false));
      }

      nextTick(() => {
        if (store.data.rowsSelected === null) {
          e.target.indeterminate = true;
        } else {
          e.target.checked = store.data.rowsSelected;
        }
      });
    };

    const visible = (field: any) => {
      const config = field?.config;

      return (
        (typeof config.visible == "function"
          ? config.visible()
          : config.visible) && !config.hide
      );
    };

    const getField = (fieldKey: string) => store.state.fields[fieldKey];

    const sortedFields = props.controller.sortedFields;

    return {
      config,
      store,
      sortingFields,
      getStyleTh,
      getThClasses,
      onClickCell,
      onOpenMenu,
      selectRows,
      visible,
      getField,
      sortedFields,
      // Resize
      isResizing,
      currentResizeField,
      onStartResize,
      // Drag-and-drop
      onDragStart,
      onDragOver,
      onDragLeave,
      onDrop,
      onDragEnd,
    };
  },
});
</script>

<style scoped>
.row-checkbox {
  position: relative;
  left: 0px;
  opacity: 1;
  top: 3px;
}

.line-through {
  text-decoration: line-through;
}

/* Drag-and-drop styles */
.th-dragging {
  opacity: 0.5;
  background: #f0f0f0 !important;
}

.drop-indicator-before {
  box-shadow: inset 2px 0 0 #007bff;
}

.drop-indicator-after {
  box-shadow: inset -2px 0 0 #007bff;
}

/* Make th draggable */
th[draggable="true"] {
  cursor: grab;
}

th[draggable="true"]:active {
  cursor: grabbing;
}

.sticky-header {
  background-color: #f8f9fa;
}

.sticky-header th {
  position: sticky;
  top: 0;
  z-index: 20;
  background-color: inherit;
}
</style>
